/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  type ConfigurationWithoutRequest,
  type ESSCostingConfiguration,
  type FIMOConfiguration,
  isCloseTo,
  PlantConfiguration,
  type PVCostingConfiguration,
  type PVSimConfiguration,
} from '@poweralpha/common';
import * as z from 'zod';
import { multiServiceCodTest } from '../validation-utils';

const sharedLossTolerance = { rtol: 1e-5, atol: 1e-8 } as const;

export type PaSimulateForm = z.input<typeof paSimulateFormSchema>;
export type PaSimulateFormParsed = z.infer<typeof paSimulateFormSchema>;

export const defaultPaSimulateForm: PaSimulateForm = {
  pv: { config: null },
  pvsim: { config: null },
  ess: { config: null },
  fimo: { config: null },
};

export const paSimulateFormSchema = z
  .object({
    pv: z.object({
      config: z.custom<ConfigurationWithoutRequest>().nullable(),
      request: z.custom<PVCostingConfiguration['request']>().optional().nullable(),
    }),
    pvsim: z.object({
      config: z.custom<ConfigurationWithoutRequest>().nullable(),
      request: z.custom<PVSimConfiguration['request']>().optional().nullable(),
    }),
    ess: z.object({
      config: z.custom<ConfigurationWithoutRequest>().nullable(),
      request: z.custom<ESSCostingConfiguration['request']>().optional().nullable(),
    }),
    fimo: z.object({
      config: z.custom<ConfigurationWithoutRequest>().nullable(),
      request: z.custom<FIMOConfiguration['request']>().optional().nullable(),
    }),
  })
  .check((ctx) => {
    const { pv, pvsim, ess, fimo } = ctx.value;

    const plantConfig: Array<PlantConfiguration> = [];
    if (pv.config) {
      plantConfig.push(PlantConfiguration.PV);
    }

    if (ess.config) {
      plantConfig.push(PlantConfiguration.BESS);
    }

    if (!plantConfig.length) {
      ctx.issues.push(
        {
          code: 'custom',
          message: 'At least one config must be selected',
          path: ['ess', 'config'],
          input: ess.config,
        },
        {
          code: 'custom',
          message: 'At least one config must be selected',
          path: ['pv', 'config'],
          input: pv.config,
        },
      );
      return;
    }

    checkPaConfigs(pv, pvsim, ess, fimo, plantConfig, ctx.issues);
  });

export type PorForm = z.infer<typeof porFormSchema>;

export const porFormSchema = z
  .object({
    is_por: z.boolean(),
    is_abd: z.boolean(),
    notes: z.string().nullable(),
  })
  .check((ctx) => {
    if (ctx.value.is_abd && ctx.value.is_por) {
      ctx.issues.push({
        code: 'custom',
        message: "As-Built can't be checked when POR Design is checked",
        path: ['is_abd'],
        input: ctx.value.is_abd,
      });
    }
  });

export const checkPaConfigs = (
  pv: PaSimulateForm['pv'],
  pvsim: PaSimulateForm['pvsim'],
  ess: PaSimulateForm['ess'],
  fimo: PaSimulateForm['fimo'],
  plantConfiguration: Array<PlantConfiguration>,
  issues: Array<z.core.$ZodRawIssue>,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: probably fine
) => {
  const hasPvConfig = Boolean(pv.config);
  const hasEssConfig = Boolean(ess.config);

  const fimoPlantConfiguration = fimo.request?.base.plant_configuration ?? null;
  const fimoHasPv = fimoPlantConfiguration?.includes(PlantConfiguration.PV) ?? false;
  const fimoHasEss = fimoPlantConfiguration?.includes(PlantConfiguration.BESS) ?? false;

  const plantTypeMatch = (() => {
    if (!fimo.request) return true;
    if (!Array.isArray(fimoPlantConfiguration)) return false;
    return fimoHasPv === hasPvConfig && fimoHasEss === hasEssConfig;
  })();

  const getPlantTypeErrorMessage = (hasPv: boolean, hasEss: boolean): string => {
    if (hasPv && hasEss) return 'FIMO plant configuration is PV+BESS';
    if (hasPv) return 'FIMO plant configuration is PV Only';
    if (hasEss) return 'FIMO plant configuration is BESS Only';
    return 'Invalid FIMO plant configuration';
  };

  if (!plantTypeMatch) {
    issues.push({
      code: 'custom',
      message: getPlantTypeErrorMessage(fimoHasPv, fimoHasEss),
      path: ['fimo', 'config'],
      input: fimo.config,
    });
  }

  const codMatch = multiServiceCodTest(plantConfiguration, {
    pv: pv.request?.cod ? new Date(pv.request.cod) : null,
    ess: ess.request?.project.cod ? new Date(ess.request.project.cod) : null,
    fimo: fimo.request?.base.cod ? new Date(fimo.request.base.cod) : null,
  });
  if (!codMatch) {
    issues.push(
      {
        code: 'custom',
        message: 'CODs for PV, ESS, and FIMO must match',
        path: ['pv', 'config'],
        input: pv.config,
      },
      {
        code: 'custom',
        message: 'CODs for PV, ESS, and FIMO must match',
        path: ['pv', 'request'],
        input: pv.request,
      },
      {
        code: 'custom',
        message: 'CODs for PV, ESS, and FIMO must match',
        path: ['ess', 'config'],
        input: ess.config,
      },
      {
        code: 'custom',
        message: 'CODs for PV, ESS, and FIMO must match',
        path: ['ess', 'request'],
        input: ess.request,
      },
    );
  }

  if (ess.request && pvsim.request) {
    const txmLossesMatch = isCloseTo(
      ess.request.losses?.txm_loss ?? 0,
      pvsim.request.losses?.transmission_line ?? 0,
      sharedLossTolerance,
    );
    const mptLossesMatch = isCloseTo(
      ess.request.losses?.main_power_transformer ?? 0,
      pvsim.request.losses?.hv_transformer?.total_loss_full_load_percent ?? 0,
      sharedLossTolerance,
    );
    const reactiveMptLossesMatch = isCloseTo(
      ess.request.losses?.x_trans_hv ?? 0,
      pvsim.request.losses?.hv_transformer?.xr_short_circuit_voltage_percent ?? 0,
      sharedLossTolerance,
    );
    if (ess.request.project.pv_size === 0) {
      issues.push(
        {
          code: 'custom',
          message: 'ESS should not include HV for PV+S projects',
          path: ['ess', 'request'],
          input: ess.request,
        },
        {
          code: 'custom',
          message: 'ESS should not include HV for PV+S projects',
          path: ['ess', 'config'],
          input: ess.config,
        },
      );
      return;
    }
    if (!txmLossesMatch || !mptLossesMatch || !reactiveMptLossesMatch) {
      issues.push(
        {
          code: 'custom',
          message: 'Losses for PVSim and ESS must match',
          path: ['ess', 'request'],
          input: ess.request,
        },
        {
          code: 'custom',
          message: 'Losses for PVSim and ESS must match',
          path: ['ess', 'config'],
          input: ess.config,
        },
      );
    }
  }
};
